import Quill from 'quill';
import ToolbarModule from 'quill/modules/toolbar';

import { COMMENT_ATTACHMENT_ALLOWED_TYPES, COMMENT_ATTACHMENT_MAX_FILE_SIZE } from '../comments.utils';

export interface RejectedFile {
  file: File;
  reason: 'type' | 'size';
}

export class AttachmentToolbarQuillModule {
  private quill: Quill;
  private attachButton: HTMLButtonElement | null = null;
  private fileInput: HTMLInputElement | null = null;

  public onFilesSelected?: (files: File[]) => void;
  public onFilesRejected?: (rejected: RejectedFile[]) => void;

  constructor(quill: Quill) {
    this.quill = quill;
    this.addAttachButton();
    this.createFileInput();
  }

  private addAttachButton(): void {
    const toolbar = this.getToolbarModule()?.container;
    if (!toolbar) return;

    const formatGroup = document.createElement('span');
    formatGroup.classList.add('ql-formats');

    this.attachButton = document.createElement('button');
    this.attachButton.type = 'button';
    this.attachButton.classList.add('ql-attach');
    this.attachButton.title = 'Attach file';
    this.attachButton.innerHTML = `📎`;
    this.attachButton.addEventListener('click', () => this.fileInput?.click());

    formatGroup.appendChild(this.attachButton);
    toolbar.appendChild(formatGroup);
  }

  private createFileInput(): void {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.multiple = true;
    this.fileInput.accept = COMMENT_ATTACHMENT_ALLOWED_TYPES.join(',');
    this.fileInput.style.display = 'none';
    this.fileInput.addEventListener('change', () => {
      const files = Array.from(this.fileInput!.files || []);
      if (!files.length) return;

      const { accepted, rejected } = this.validate(files);

      if (rejected.length && this.onFilesRejected) {
        this.onFilesRejected(rejected);
      }
      if (accepted.length && this.onFilesSelected) {
        this.onFilesSelected(accepted);
      }

      // Reset so the same file can be selected again
      this.fileInput!.value = '';
    });
    document.body.appendChild(this.fileInput);
  }

  private validate(files: File[]): { accepted: File[]; rejected: RejectedFile[] } {
    const accepted: File[] = [];
    const rejected: RejectedFile[] = [];

    for (const file of files) {
      if (!COMMENT_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
        rejected.push({ file, reason: 'type' });
      } else if (file.size > COMMENT_ATTACHMENT_MAX_FILE_SIZE) {
        rejected.push({ file, reason: 'size' });
      } else {
        accepted.push(file);
      }
    }

    return { accepted, rejected };
  }

  private getToolbarModule() {
    return this.quill.getModule('toolbar') as ToolbarModule;
  }

  destroy(): void {
    this.fileInput?.remove();
  }
}
